import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, EyeOff } from "lucide-react";
import { aapi, apiErr, useA } from "./lib";
import { UploadButton } from "./Widgets";

export const HOME_KEYS = ["about", "spotlight", "featured", "glance", "stats", "tabs", "types", "cities", "upcoming", "services", "why", "recommendations", "how", "news", "social", "cta"];
const LABELS = {
  about: ["About Landmark Future intro", "نبذة عن لاندمارك فيوتشر"], spotlight: ["Flagship project spotlight (Al Hamra)", "المشروع الرائد (الحمراء)"],
  featured: ["Featured developments", "المشاريع المميزة"], glance: ["Company at a glance (live counters)", "الشركة في لمحة (أرقام مباشرة)"],
  stats: ["Verified company stats (hidden while empty)", "إحصاءات موثقة (مخفية إن كانت فارغة)"], tabs: ["Curated property tabs", "تبويبات العقارات"],
  types: ["Browse by property type", "تصفح حسب النوع"], cities: ["Explore by city + map", "استكشف حسب المدينة + الخريطة"],
  upcoming: ["Upcoming projects + investment + calculator", "المشاريع القادمة + الاستثمار + الحاسبة"], services: ["Our services", "خدماتنا"],
  why: ["Why Landmark Future", "لماذا لاندمارك فيوتشر"], recommendations: ["Recommendations / popular properties", "المقترحات / العقارات الرائجة"],
  how: ["How it works", "كيف نعمل"], news: ["News & insights", "الأخبار والرؤى"], social: ["Testimonials & partners (verified only)", "آراء العملاء والشركاء (الموثقة فقط)"],
  cta: ["Book a private viewing band", "شريط حجز المعاينة"],
};
const ABOUT = ["home_about_title_en", "home_about_title_ar", "home_about_body_en", "home_about_body_ar", "home_about_image"];

const merge = (stored = []) => {
  const known = stored.filter((s) => HOME_KEYS.includes(s.key));
  return [...known, ...HOME_KEYS.filter((k) => !known.some((s) => s.key === k)).map((key) => ({ key, visible: true }))];
};

function SectionRow({ s, i, last, move, toggle }) {
  const a = useA();
  return (
    <li data-testid={`homepage-section-${s.key}`} className={`flex items-center gap-3 rounded-sm border border-ivory/10 px-4 py-3 ${s.visible ? "" : "opacity-50"}`}>
      <span className="w-6 text-xs text-ivory/40">{i + 1}</span>
      <span className="flex-1 text-sm">{a(...LABELS[s.key])}</span>
      <button type="button" onClick={() => toggle(i)} data-testid={`homepage-toggle-${s.key}`} aria-pressed={s.visible} className="chip !px-3 !py-1.5">{s.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}{s.visible ? a("Shown", "ظاهر") : a("Hidden", "مخفي")}</button>
      <button type="button" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`homepage-up-${s.key}`} aria-label="Move up" className="chip !px-2 !py-1.5 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
      <button type="button" disabled={last} onClick={() => move(i, 1)} data-testid={`homepage-down-${s.key}`} aria-label="Move down" className="chip !px-2 !py-1.5 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
    </li>
  );
}

function AboutFields({ v, set }) {
  const a = useA();
  const inp = (k, label, area, rtl) => (
    <label className="block text-xs text-ivory/60">{label}
      {area ? <textarea rows={5} dir={rtl ? "rtl" : "ltr"} className="field mt-1" value={v[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`homepage-${k}`} />
        : <input dir={rtl ? "rtl" : "ltr"} className="field mt-1" value={v[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`homepage-${k}`} />}
    </label>
  );
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {inp("home_about_title_en", a("Title (EN)", "العنوان (إنجليزي)"))}{inp("home_about_title_ar", a("Title (AR)", "العنوان (عربي)"), false, true)}
      {inp("home_about_body_en", a("Story (EN)", "النص (إنجليزي)"), true)}{inp("home_about_body_ar", a("Story (AR)", "النص (عربي)"), true, true)}
      <div className="md:col-span-2">{inp("home_about_image", a("Image URL", "رابط الصورة"))}<div className="mt-2"><UploadButton kind="image" onDone={(url) => set("home_about_image", url)} testid="homepage-about-upload" /></div></div>
    </div>
  );
}

export function HomepagePage() {
  const a = useA();
  const [v, setV] = useState(null);
  useEffect(() => { aapi.get("/admin/settings").then((r) => setV({ ...Object.fromEntries(ABOUT.map((k) => [k, r.data[k]])), home_sections: merge(r.data.home_sections) })); }, []);
  if (!v) return <p>…</p>;
  const secs = v.home_sections;
  const move = (i, d) => { const n = [...secs]; [n[i], n[i + d]] = [n[i + d], n[i]]; setV({ ...v, home_sections: n }); };
  const toggle = (i) => setV({ ...v, home_sections: secs.map((s, j) => (j === i ? { ...s, visible: !s.visible } : s)) });
  const save = async () => { try { await aapi.put("/admin/homepage", v); toast.success(a("Homepage saved", "تم حفظ الصفحة الرئيسية")); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div data-testid="admin-homepage" className="space-y-10">
      <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="h-display text-3xl">{a("Homepage Sections", "أقسام الصفحة الرئيسية")}</h1><button type="button" className="btn-gold !py-2.5" onClick={save} data-testid="homepage-save">{a("Save", "حفظ")}</button></div>
      <section>
        <p className="mb-4 text-sm text-ivory/55">{a("The hero slider + search always stay first and the footer last. Reorder or hide everything in between.", "تبقى شرائح الواجهة والبحث في الأعلى والتذييل في الأسفل دائماً. أعد ترتيب الأقسام بينهما أو أخفها.")}</p>
        <ol className="space-y-2">{secs.map((s, i) => (<SectionRow key={s.key} s={s} i={i} last={i === secs.length - 1} move={move} toggle={toggle} />))}</ol>
      </section>
      <section><h2 className="h-display mb-4 text-xl">{a("About intro", "نبذة الشركة")}</h2><AboutFields v={v} set={(k, val) => setV({ ...v, [k]: val })} /></section>
    </div>
  );
}
